import React, { Component } from "react";
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Card,
    CardContent,
    Grid,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PresentToAllIcon from '@mui/icons-material/PresentToAll';
import SendIcon from '@mui/icons-material/Send';
import { sendMessage } from "../user/apiUser";
import { submitPresentation2027 } from "../event/apiEvents";
import Footer from './Footer';

// Committees members can submit a presentation for. Kept in sync with the
// committee tracks used elsewhere on the site (see ConferencePresentationSubmission.js).
const COMMITTEES = [
    'Business Strategy',
    'Civil',
    'Electrical',
    'Innovative Solutions',
    'Physical',
    'Technology'
];

const SESSION_FORMATS = [
    'Member Presentation',
    'Case Study',
    'Panel',
    'Open Discussion',
    'Hands-On Workshop'
];

const EXPERIENCE_LEVELS = [
    'First time presenting at SDSIC',
    'Presented 1 to 4 times at the SDSIC',
    'Presented 5+ times at the SDSIC',
    'Presented at many other conferences'
];

const AUDIENCE_LEVELS = [
    'Introductory',
    'Intermediate',
    'Advanced',
    'All Levels'
];

const ADMIN_EMAIL = 'trevor@sdsconsortium.com';

class CallForPresentations2027 extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Speaker / contact
            firstName: '',
            lastName: '',
            email: '',
            company: '',
            jobTitle: '',
            linkedIn: '',
            bio: '',
            // Presentation
            committee: 'Business Strategy',
            secondCommittee: '',
            thirdCommittee: '',
            sessionFormat: 'Member Presentation',
            audienceLevel: 'All Levels',
            experience: 'First time presenting at SDSIC',
            proposalTitle: '',
            proposal: '',
            learningObjectives: '',
            archiveConsent: false,
            // UI
            error: '',
            success: false,
            expandedGuideline: 'panel0',
            expandedTip: 'panelA'
        };
    }

    handleChange = name => event => {
        this.setState({
            error: "",
            [name]: event.target.value
        });
    };

    handleCheckboxChange = name => event => {
        this.setState({
            error: "",
            [name]: event.target.checked
        });
    };

    // Changing the primary committee may invalidate the second/third choices
    // (they must stay unique); clear any downstream choice that now collides.
    handlePrimaryCommitteeChange = event => {
        const value = event.target.value;
        this.setState(prev => ({
            error: "",
            committee: value,
            secondCommittee: prev.secondCommittee === value ? '' : prev.secondCommittee,
            thirdCommittee: prev.thirdCommittee === value ? '' : prev.thirdCommittee
        }));
    };

    // Changing the second committee may invalidate the third choice.
    handleSecondCommitteeChange = event => {
        const value = event.target.value;
        this.setState(prev => ({
            error: "",
            secondCommittee: value,
            thirdCommittee: prev.thirdCommittee === value ? '' : prev.thirdCommittee
        }));
    };

    handleAccordionChange = (type) => (panel) => (event, isExpanded) => {
        if (type === 'guideline') {
            this.setState({ expandedGuideline: isExpanded ? panel : false });
        } else {
            this.setState({ expandedTip: isExpanded ? panel : false });
        }
    };

    submitProposal = async e => {
        e.preventDefault();
        const {
            firstName, lastName, email, company, jobTitle, linkedIn, bio,
            committee, secondCommittee, thirdCommittee, sessionFormat, audienceLevel,
            experience, proposalTitle, proposal, learningObjectives, archiveConsent
        } = this.state;

        // Basic validation of the required fields
        if (firstName.trim() === "") {
            return this.setState({ error: "First name is required!" });
        }
        if (lastName.trim() === "") {
            return this.setState({ error: "Last name is required!" });
        }
        if (email.trim() === "") {
            return this.setState({ error: "Email is required!" });
        }
        if (proposalTitle.trim() === "") {
            return this.setState({ error: "Presentation title can't be blank!" });
        }
        if (proposal.trim() === "") {
            return this.setState({ error: "Presentation description can't be blank!" });
        }
        if (secondCommittee === "") {
            return this.setState({ error: "Please select a second choice committee." });
        }
        // The three committee choices must all be unique
        const chosen = [committee, secondCommittee, thirdCommittee].filter(Boolean);
        if (new Set(chosen).size !== chosen.length) {
            return this.setState({ error: "Your committee choices must each be different." });
        }
        if (!archiveConsent) {
            return this.setState({ error: "You must agree that your presentation will become part of the permanent website archive." });
        }

        const submissionRows = `
            <p>Speaker: <b>${firstName} ${lastName}</b></p>
            <p>Email: <b>${email}</b></p>
            <p>Company: <b>${company || 'N/A'}</b></p>
            <p>Job Title: <b>${jobTitle || 'N/A'}</b></p>
            <p>LinkedIn Profile: <b>${linkedIn || 'N/A'}</b></p>
            <p>Presentation Title: <b>${proposalTitle}</b></p>
            <p>Primary Committee: <b>${committee}</b></p>
            <p>Second Choice Committee: <b>${secondCommittee}</b></p>
            <p>Third Choice Committee: <b>${thirdCommittee || 'N/A'}</b></p>
            <p>Session Format: <b>${sessionFormat}</b></p>
            <p>Audience Level: <b>${audienceLevel}</b></p>
            <p>Speaking Experience: <b>${experience}</b></p>
            <p>Speaker Bio: <b>${bio || 'N/A'}</b></p>
            <p>Presentation Description: <b>${proposal}</b></p>
            <p>Learning Objectives / Takeaways: <b>${learningObjectives || 'N/A'}</b></p>
            <p>Permanent Archive Consent: <b>Agreed</b></p>`;

        // Confirmation email to the submitter
        const usersMessage = {
            "subject": 'Thank you for your SDSIC 2027 Conference presentation submission',
            "from": "donotreply@sdsconsortium.com",
            // Object form, not "Name <addr>" - firstName/lastName come from the
            // public form, and a name containing <someone@else.com> would
            // otherwise replace the real recipient.
            "to": [{ "name": `${firstName} ${lastName}`, "address": `${email}` }],
            "bcc": [{ "name": "Trevor Scullion", "address": ADMIN_EMAIL }],
            "html": `<html><body>Hi ${firstName},<br/>
                <p>Thank you for submitting a presentation for the <b>2027 SDSIC Conference in New Orleans</b>.</p>
                <p>Here is a copy of what you submitted:</p>
                ${submissionRows}
                <p>The committee chair and co-chair will review submissions and respond accordingly.
                Please review our guidelines to understand more about the selection process.</p>
                <p>Thank you for helping advance the SDSIC in 2027!</p>
                </body></html>`
        };

        // Notification email to the SDSIC admin
        const adminMessage = {
            "subject": `New 2027 Presentation Submission: ${proposalTitle}`,
            "from": "donotreply@sdsconsortium.com",
            "to": [{ "name": "Trevor Scullion", "address": ADMIN_EMAIL }],
            "html": `<html><body>
                <p>A new presentation was submitted for the 2027 SDSIC Conference.</p>
                ${submissionRows}
                </body></html>`
        };

        // Persist the submission to the presentations_27 collection first.
        // If the save fails, surface the error and don't send emails or reset.
        const submission = {
            firstName, lastName, email, company, jobTitle, linkedIn, bio,
            committee, secondCommittee, thirdCommittee, sessionFormat, audienceLevel,
            experience, proposalTitle, proposal, learningObjectives, archiveConsent
        };

        const saved = await submitPresentation2027(submission);
        if (!saved || saved.error) {
            return this.setState({
                error: (saved && saved.error) || "Sorry, we couldn't save your submission. Please try again."
            });
        }

        // Fire off both emails
        sendMessage(usersMessage);
        sendMessage(adminMessage);

        this.setState({
            firstName: '',
            lastName: '',
            email: '',
            company: '',
            jobTitle: '',
            linkedIn: '',
            bio: '',
            committee: 'Business Strategy',
            secondCommittee: '',
            thirdCommittee: '',
            sessionFormat: 'Member Presentation',
            audienceLevel: 'All Levels',
            experience: 'First time presenting at SDSIC',
            proposalTitle: '',
            proposal: '',
            learningObjectives: '',
            archiveConsent: false,
            error: '',
            success: true
        });

        // Bring the confirmation into view
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    render() {
        const {
            firstName, lastName, email, company, jobTitle, linkedIn, bio,
            committee, secondCommittee, thirdCommittee, sessionFormat, audienceLevel,
            experience, proposalTitle, proposal, learningObjectives, archiveConsent,
            error, success, expandedGuideline, expandedTip
        } = this.state;

        const guidelines = [
            {
                title: 'Be clear',
                content: "What you submit is what you'll present. Nothing upsets attendees more than signing up for a session that's not as advertised."
            },
            {
                title: 'Be enthusiastic',
                content: 'Make sure your content is not only relevant but also exciting. What invaluable information will you share during the session?'
            },
            {
                title: 'Be specific',
                content: 'Identify your target audience and the roles that will specifically benefit from this session. Make sure your presentation stays on topic and is relevant to the session category.'
            },
            {
                title: 'Be original',
                content: 'Attendees want to see new presentations that cover the latest innovations in technology. Take the time to create well-written titles, abstracts, and outlines, and select the appropriate audience and expertise level.'
            },
            {
                title: 'Be educational',
                content: "Be sure your proposal doesn't sound like a sales pitch but rather like an exciting opportunity for attendees to learn something new."
            }
        ];

        const tips = [
            {
                title: 'Include relevant details',
                content: 'Please provide any pertinent information that will help us understand your selection of discipline. This helps us determine the appropriate committee track for your presentation.'
            },
            {
                title: 'No product promotion allowed',
                content: 'All papers and presentations must be noncommercial. At no time is it permissible for presenters to use their time slot to advertise or promote a product, service, or company.'
            }
        ];

        return (
            <>
                {/* Hero Section */}
                <Box sx={{ backgroundColor: '#003E7E', color: 'white', py: 6, mb: 4 }}>
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
                            <CampaignIcon sx={{ fontSize: 64 }} />
                            <Box>
                                <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                                    Call for Presentations — 2027 SDSIC Conference
                                </Typography>
                                <Typography variant="h5" sx={{ opacity: 0.9 }}>
                                    New Orleans, 2027
                                </Typography>
                            </Box>
                        </Box>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 2 }}>
                            SDSIC is dedicated to supporting the success of our members and their engagement with professional engineers and designers.
                            Our roles as professionals in the Electric Utility Industry often require innovation, problem solving, entrepreneurial spirit,
                            nimble growth, and collaboration as we respond to industry and community needs.
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.7 }}>
                            We want to hear about your projects and efforts to improve the Power Grid and the lives of people in your community and beyond.
                            Submit a presentation below for any one of our committees.
                        </Typography>
                    </Container>
                </Box>

                <Container maxWidth="lg" sx={{ mb: 6 }}>
                    {/* How It Works */}
                    <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003E7E', mb: 3 }}>
                            How It Works
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 3 }}>
                            Our committees will evaluate your presentation submission and make selections based on topic, content, and time availability.
                            All authors will be notified after the selection process. You may submit a presentation for any one of our committees.
                        </Typography>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E', mb: 3 }}>
                            Why Present at the SDSIC 2027 Conference?
                        </Typography>
                        <List>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Share Your Success"
                                    secondary="Get the chance to share your project successes with an international audience"
                                    primaryTypographyProps={{ fontWeight: 600 }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Industry Recognition"
                                    secondary="Recognition on the 1,900+ member SDSIC website"
                                    primaryTypographyProps={{ fontWeight: 600 }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Discounted Conference Pass"
                                    secondary="Selected presenters receive a discounted conference pass!"
                                    primaryTypographyProps={{ fontWeight: 600, color: '#FF6600' }}
                                />
                            </ListItem>
                            <ListItem>
                                <ListItemIcon>
                                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Permanent Archive"
                                    secondary="Your presentation will be published on our website for all time"
                                    primaryTypographyProps={{ fontWeight: 600 }}
                                />
                            </ListItem>
                        </List>
                    </Paper>

                    {/* Session Formats */}
                    <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 3 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003E7E', mb: 3 }}>
                            Session Formats
                        </Typography>
                        <List>
                            <ListItem sx={{ display: 'block', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 1 }}>
                                    Open Discussion
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                    Moderator-facilitated, in-depth conversation with participants serving as key contributors around a specific issue or topic concerning electric utilities.
                                    Participants are responsible for much of the content shared, as well as which issues receive the most discussion.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ display: 'block', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 1 }}>
                                    Panel
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                    Moderator-led discussion with experts in the field with an opportunity for audience Q&A. Features multiple SDSIC members, Electric Utility employees,
                                    or other experts in conversation with each other to advance participant understanding about an important issue in our industry.
                                </Typography>
                            </ListItem>
                            <ListItem sx={{ display: 'block' }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 1 }}>
                                    Member Presentation
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                    Formal presentation by an expert, often featuring a visual component (e.g., PowerPoint, etc.) designed to inform the audience about a specific issue or topic
                                    and demonstrate key components of a particular approach. Selling is NOT allowed.
                                </Typography>
                            </ListItem>
                        </List>
                    </Paper>

                    {/* Tips and Guidelines */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                            Tips and Guidelines
                        </Typography>
                        <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 4 }}>
                            Submissions should tell inspiring stories that illustrate how you are using solutions, specific technology, unique workflows
                            or tell your project story and how it positively impacted your communities or industries.
                        </Typography>

                        <Grid container spacing={3}>
                            {/* Submission Guidelines */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                                    Submission Guidelines
                                </Typography>
                                {guidelines.map((guideline, i) => {
                                    const panelId = `panel${i}`;
                                    const isExpanded = expandedGuideline === panelId;
                                    return (
                                        <Accordion
                                            key={i}
                                            expanded={isExpanded}
                                            onChange={this.handleAccordionChange('guideline')(panelId)}
                                            elevation={2}
                                            sx={{
                                                mb: 1,
                                                borderRadius: 2,
                                                '&:before': { display: 'none' },
                                                border: isExpanded ? '2px solid #003E7E' : 'none'
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: isExpanded ? '#003E7E' : 'rgba(0, 62, 126, 0.05)',
                                                    color: isExpanded ? 'white' : '#003E7E',
                                                    '&:hover': {
                                                        backgroundColor: isExpanded ? '#003E7E' : 'rgba(0, 62, 126, 0.1)'
                                                    }
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {guideline.title}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 2, backgroundColor: 'white' }}>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {guideline.content}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Grid>

                            {/* Helpful Tips */}
                            <Grid item xs={12} md={6}>
                                <Typography variant="h5" sx={{ fontWeight: 600, color: '#003E7E', mb: 2 }}>
                                    Helpful Tips
                                </Typography>
                                {tips.map((tip, i) => {
                                    const panelId = `panelA${i}`;
                                    const isExpanded = expandedTip === panelId;
                                    return (
                                        <Accordion
                                            key={i}
                                            expanded={isExpanded}
                                            onChange={this.handleAccordionChange('tip')(panelId)}
                                            elevation={2}
                                            sx={{
                                                mb: 1,
                                                borderRadius: 2,
                                                '&:before': { display: 'none' },
                                                border: isExpanded ? '2px solid #FF6600' : 'none'
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                sx={{
                                                    backgroundColor: isExpanded ? '#FF6600' : 'rgba(255, 102, 0, 0.05)',
                                                    color: isExpanded ? 'white' : '#FF6600',
                                                    '&:hover': {
                                                        backgroundColor: isExpanded ? '#e55a00' : 'rgba(255, 102, 0, 0.1)'
                                                    }
                                                }}
                                            >
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {tip.title}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ p: 2, backgroundColor: 'white' }}>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {tip.content}
                                                </Typography>
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Submission Form */}
                    <Card elevation={3} sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ backgroundColor: '#FF6600', color: 'white', p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <PresentToAllIcon sx={{ fontSize: 40 }} />
                                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                                    Submit a Presentation
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 4 }}>
                            {/* Success Alert */}
                            {success && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                        Thank you for your submission!
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Don't forget to check your inbox and your spam folder for a confirmation email.
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        You will hear back from the committee as soon as decisions are made on proposed presentations.
                                    </Typography>
                                    <Typography variant="body2">
                                        Thank you!
                                    </Typography>
                                </Alert>
                            )}

                            {/* Error Alert */}
                            {error && (
                                <Alert severity="error" sx={{ mb: 3 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" onSubmit={this.submitProposal}>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mt: 1, mb: 1 }}>
                                    Speaker Information
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="First Name"
                                            value={firstName}
                                            onChange={this.handleChange("firstName")}
                                            margin="normal"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Last Name"
                                            value={lastName}
                                            onChange={this.handleChange("lastName")}
                                            margin="normal"
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    required
                                    label="Your Email Address"
                                    type="email"
                                    value={email}
                                    onChange={this.handleChange("email")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="name@example.com"
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Company / Organization"
                                            value={company}
                                            onChange={this.handleChange("company")}
                                            margin="normal"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Job Title"
                                            value={jobTitle}
                                            onChange={this.handleChange("jobTitle")}
                                            margin="normal"
                                            variant="outlined"
                                        />
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    label="LinkedIn Profile"
                                    value={linkedIn}
                                    onChange={this.handleChange("linkedIn")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="https://www.linkedin.com/in/your-profile"
                                />

                                <TextField
                                    fullWidth
                                    label="Speaker Bio (400 words max)"
                                    multiline
                                    rows={3}
                                    value={bio}
                                    onChange={this.handleChange("bio")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="A brief bio about the speaker"
                                />

                                <FormControl fullWidth margin="normal" variant="outlined">
                                    <InputLabel>Speaking Experience</InputLabel>
                                    <Select
                                        value={experience}
                                        onChange={this.handleChange("experience")}
                                        label="Speaking Experience"
                                    >
                                        {EXPERIENCE_LEVELS.map(level => (
                                            <MenuItem key={level} value={level}>{level}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Divider sx={{ my: 3 }} />

                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#003E7E', mb: 1 }}>
                                    Presentation Details
                                </Typography>

                                <FormControl fullWidth margin="normal" variant="outlined">
                                    <InputLabel>Committee to Present In</InputLabel>
                                    <Select
                                        value={committee}
                                        onChange={this.handlePrimaryCommitteeChange}
                                        label="Committee to Present In"
                                    >
                                        {COMMITTEES.map(c => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth required margin="normal" variant="outlined">
                                    <InputLabel>Second Choice Committee</InputLabel>
                                    <Select
                                        value={secondCommittee}
                                        onChange={this.handleSecondCommitteeChange}
                                        label="Second Choice Committee"
                                    >
                                        {COMMITTEES.filter(c => c !== committee).map(c => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth margin="normal" variant="outlined">
                                    <InputLabel>Third Choice Committee (optional)</InputLabel>
                                    <Select
                                        value={thirdCommittee}
                                        onChange={this.handleChange("thirdCommittee")}
                                        label="Third Choice Committee (optional)"
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {COMMITTEES.filter(c => c !== committee && c !== secondCommittee).map(c => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal" variant="outlined">
                                            <InputLabel>Session Format</InputLabel>
                                            <Select
                                                value={sessionFormat}
                                                onChange={this.handleChange("sessionFormat")}
                                                label="Session Format"
                                            >
                                                {SESSION_FORMATS.map(f => (
                                                    <MenuItem key={f} value={f}>{f}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth margin="normal" variant="outlined">
                                            <InputLabel>Audience Level</InputLabel>
                                            <Select
                                                value={audienceLevel}
                                                onChange={this.handleChange("audienceLevel")}
                                                label="Audience Level"
                                            >
                                                {AUDIENCE_LEVELS.map(a => (
                                                    <MenuItem key={a} value={a}>{a}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <TextField
                                    fullWidth
                                    required
                                    label="Presentation Title (150 words max)"
                                    multiline
                                    rows={2}
                                    value={proposalTitle}
                                    onChange={this.handleChange("proposalTitle")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="Enter your presentation title as you'd like it to appear in the schedule"
                                />

                                <TextField
                                    fullWidth
                                    required
                                    label="Presentation Description / Abstract (350 words max)"
                                    multiline
                                    rows={6}
                                    value={proposal}
                                    onChange={this.handleChange("proposal")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="Write a one or two paragraph abstract describing your presentation."
                                />

                                <TextField
                                    fullWidth
                                    label="Key Learning Objectives / Takeaways"
                                    multiline
                                    rows={3}
                                    value={learningObjectives}
                                    onChange={this.handleChange("learningObjectives")}
                                    margin="normal"
                                    variant="outlined"
                                    placeholder="What will attendees learn or take away from your session?"
                                />

                                <FormControlLabel
                                    sx={{ mt: 3, alignItems: 'flex-start' }}
                                    control={
                                        <Checkbox
                                            checked={archiveConsent}
                                            onChange={this.handleCheckboxChange("archiveConsent")}
                                            sx={{ pt: 0, color: '#003E7E', '&.Mui-checked': { color: '#FF6600' } }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                            I understand and agree that, if selected, my presentation materials will become
                                            part of the permanent archive published on the SDSIC website. <span style={{ color: '#d32f2f' }}>*</span>
                                        </Typography>
                                    }
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    size="large"
                                    startIcon={<SendIcon />}
                                    fullWidth
                                    sx={{
                                        mt: 3,
                                        backgroundColor: '#FF6600',
                                        py: 1.5,
                                        fontSize: '1.1rem',
                                        '&:hover': {
                                            backgroundColor: '#e55a00'
                                        }
                                    }}
                                >
                                    Submit Presentation
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Container>

                <Footer />
            </>
        );
    }
}

export default CallForPresentations2027;
